import { useState, useEffect } from "react";
import { Key, RefreshCw, PlusCircle, AlertTriangle, ExternalLink, Copy, Eye, EyeOff, Loader2, Shield, Webhook } from "lucide-react";
import { toast } from "sonner";
import { Shimmer, DeveloperSettingsShimmer } from "@/components/ui/shimmer";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { getAPIToken, getWhitelistedIPs, getWebhookConfig, updateWhitelistedIP, generateAPIToken, updateWebhookConfig, type WhitelistedIPData, type WebhookConfig, type GeneratedToken } from "@/utils/developer-api";

export default function DeveloperSettingsPage() {
  const { userId, token } = useAuth();

  const [activeTab, setActiveTab] = useState("api-token");

  const [apiToken, setApiToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [confirmGenerate, setConfirmGenerate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newlyGenerated, setNewlyGenerated] = useState<GeneratedToken | null>(null);

  const [ipData, setIpData] = useState<WhitelistedIPData | null>(null);
  const [, setIpLoading] = useState(true);
  const [editingIp, setEditingIp] = useState(false);
  const [ipInput, setIpInput] = useState("");
  const [ipSaving, setIpSaving] = useState(false);

  const [webhookData, setWebhookData] = useState<WebhookConfig | null>(null);
  const [webhookLoading, setWebhookLoading] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(false);
  const [webhookForm, setWebhookForm] = useState({ webhook: "", secret: "", active: true, encryption: false });
  const [webhookSaving, setWebhookSaving] = useState(false);

  useEffect(() => {
    if (!userId || !token) return;
    setLoading(true);
    setIpLoading(true);
    setWebhookLoading(true);
    Promise.all([
      getAPIToken(userId, token).then(setApiToken),
      getWhitelistedIPs(userId, token).then(setIpData),
      getWebhookConfig(userId, token).then(setWebhookData),
    ])
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load settings"))
      .finally(() => { setLoading(false); setIpLoading(false); setWebhookLoading(false); });
  }, [userId, token]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast("Copied to clipboard");
  };

  const handleGenerateToken = async () => {
    if (!userId || !token) return;
    setGenerating(true);
    setConfirmGenerate(false);
    try {
      const result = await generateAPIToken(userId, token);
      setApiToken(result.clientId);
      setNewlyGenerated(result);
      setShowToken(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate token");
    } finally {
      setGenerating(false);
    }
  };

  const startEditingWebhook = (existing?: WebhookConfig | null) => {
    setWebhookForm({
      webhook: existing?.webhook ?? "",
      secret: existing?.webhook_secret_key ?? "",
      active: existing?.webhook_active ?? true,
      encryption: existing?.webhook_active_encryption ?? false,
    });
    setEditingWebhook(true);
  };

  const handleSaveWebhook = async () => {
    if (!userId || !token || !webhookForm.webhook.trim()) return;
    setWebhookSaving(true);
    try {
      await updateWebhookConfig(
        userId,
        {
          webhook: webhookForm.webhook.trim(),
          webhook_active: webhookForm.active,
          webhook_secret_key: webhookForm.secret.trim(),
          webhook_active_encryption: webhookForm.encryption,
        },
        token
      );
      setWebhookData({
        webhook: webhookForm.webhook.trim(),
        webhook_active: webhookForm.active,
        webhook_secret_key: webhookForm.secret.trim(),
        webhook_active_encryption: webhookForm.encryption,
      });
      setEditingWebhook(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save webhook");
    } finally {
      setWebhookSaving(false);
    }
  };

  const handleUpdateIp = async () => {
    if (!userId || !token || !ipInput.trim()) return;
    setIpSaving(true);
    try {
      const updated = await updateWhitelistedIP(userId, ipInput.trim(), token);
      setIpData((prev) => prev ? { ...prev, ip: updated.ip, updated_at: updated.updated_at } : prev);
      setEditingIp(false);
      toast("IP updated successfully");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update IP");
    } finally {
      setIpSaving(false);
    }
  };

  const maskedToken = apiToken
    ? apiToken.slice(0, 8) + "••••••••••••••••" + apiToken.slice(-4)
    : "";

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold">
            Developer Settings
          </h1>
          <p className="text-muted-foreground">
            API token, whitelist IPs, and webhook configuration.
          </p>
        </div>
        <a href="https://docs.superflow.run/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1 text-sm font-medium hover:bg-muted transition-colors">
          API Docs
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Shimmer loading={loading} fallback={<DeveloperSettingsShimmer />}>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="api-token"><Key className="h-3.5 w-3.5 mr-1.5" />API Token</TabsTrigger>
          <TabsTrigger value="whitelist-ip"><Shield className="h-3.5 w-3.5 mr-1.5" />Whitelist IP</TabsTrigger>
          <TabsTrigger value="webhook"><Webhook className="h-3.5 w-3.5 mr-1.5" />Webhook</TabsTrigger>
        </TabsList>

        <TabsContent value="api-token">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Key className="h-5 w-5 text-primary" />
                  <h3 className="text-base font-semibold">
                    API Token
                  </h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmGenerate(true)}
                  disabled={generating}
                >
                  {generating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : apiToken ? (
                    <>
                      <RefreshCw className="mr-1 h-4 w-4" />
                      Regenerate Token
                    </>
                  ) : (
                    <>
                      <PlusCircle className="mr-1 h-4 w-4" />
                      Generate Token
                    </>
                  )}
                </Button>
              </div>
              <span className="text-sm text-muted-foreground mb-5 block">
                Use this token to authenticate API requests. Keep it secret and never expose it in client-side code.
              </span>
              <Separator className="mb-5" />

              <div className="flex items-start gap-3 rounded-lg border border-yellow-400/50 bg-yellow-50 p-3 text-yellow-800 dark:border-yellow-500/30 dark:bg-yellow-500/10 dark:text-yellow-300 mb-5">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold mb-0.5">Security Notice</p>
                  <p className="text-xs">Keep your API tokens secure. Never share them or commit them to version control. If compromised, generate a new token immediately.</p>
                </div>
              </div>

              {newlyGenerated ? (
                /* Just generated -- show client ID + secret with warning */
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3 rounded-lg border border-yellow-400/50 bg-yellow-50 p-3 text-yellow-800 dark:border-yellow-500/30 dark:bg-yellow-500/10 dark:text-yellow-300">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <p className="text-sm">
                      Make sure to copy and store the secret securely. <strong>It won't be shown again.</strong>
                    </p>
                  </div>

                  {/* Client ID */}
                  <div>
                    <span className="text-xs text-muted-foreground font-medium mb-1 block">
                      Client ID
                    </span>
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted border">
                      <span className="text-sm font-mono flex-1 break-all">
                        {newlyGenerated.clientId}
                      </span>
                      <Button variant="ghost" size="icon" onClick={() => copyToClipboard(newlyGenerated.clientId)} title="Copy Client ID">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Client Secret */}
                  <div>
                    <span className="text-xs text-muted-foreground font-medium mb-1 block">
                      Client Secret
                    </span>
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted border border-yellow-500">
                      <span className="text-sm font-mono flex-1 break-all">
                        {newlyGenerated.clientSecret}
                      </span>
                      <Button variant="ghost" size="icon" onClick={() => copyToClipboard(newlyGenerated.clientSecret)} title="Copy Secret">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ) : apiToken ? (
                /* Existing token -- show client ID only */
                <div className="flex flex-col gap-4">
                  <div>
                    <span className="text-xs text-muted-foreground font-medium mb-1 block">
                      Client ID
                    </span>
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted border">
                      <span className={`text-sm font-mono flex-1 break-all ${showToken ? "select-all" : "select-none"}`}>
                        {showToken ? apiToken : maskedToken}
                      </span>
                      <Button variant="ghost" size="icon" onClick={() => setShowToken((p) => !p)} title={showToken ? "Hide" : "Reveal"}>
                        {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => copyToClipboard(apiToken)} title="Copy Client ID">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <Badge variant="outline" className="self-start text-green-600 border-green-600">Active</Badge>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 py-6">
                  <Key className="h-10 w-10 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground text-center">
                    No API token generated yet. Click "Generate Token" to create one.
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Confirmation Dialog */}
          <Dialog open={confirmGenerate} onOpenChange={(val) => { if (!val) setConfirmGenerate(false); }}>
            <DialogContent className="sm:max-w-xs">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  {apiToken ? "Regenerate API Token?" : "Generate API Token?"}
                </DialogTitle>
                <DialogDescription>
                  {apiToken
                    ? <>Are you sure you want to generate a new API token? This will <strong>invalidate your existing token</strong> and any integrations using it will stop working.</>
                    : "This will generate a new API token. Make sure to copy and store the secret securely after generation."
                  }
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setConfirmGenerate(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleGenerateToken}
                >
                  {apiToken ? "Yes, Regenerate" : "Generate"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="whitelist-ip">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-5 w-5 text-primary" />
                <h3 className="text-base font-semibold">Whitelist IP</h3>
              </div>
              <span className="text-sm text-muted-foreground mb-4 block">
                API access is restricted to the whitelisted IP address below.
              </span>
              <Separator className="mb-5" />

              <div className="flex items-start gap-3 rounded-lg border border-yellow-400/50 bg-yellow-50 p-3 text-yellow-800 dark:border-yellow-500/30 dark:bg-yellow-500/10 dark:text-yellow-300 mb-5">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold mb-0.5">Security Notice</p>
                  <p className="text-xs">IP whitelisting adds an extra layer of security by restricting API access to specific IP addresses. Only requests from whitelisted IPs will be allowed to access the API.</p>
                </div>
              </div>

              {ipData ? (
                <div className="flex flex-col gap-4">
                  {editingIp ? (
                    <div className="flex gap-3 items-center">
                      <div className="flex-1">
                        <Label htmlFor="ip-address" className="sr-only">IP Address</Label>
                        <Input
                          id="ip-address"
                          placeholder="e.g. 203.0.113.50"
                          value={ipInput}
                          onChange={(e) => setIpInput(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <Button
                        size="sm"
                        onClick={handleUpdateIp}
                        disabled={ipSaving || !(ipInput ?? "").trim()}
                        className="whitespace-nowrap"
                      >
                        {ipSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingIp(false)}
                        disabled={ipSaving}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted border">
                      <span className="text-sm font-mono font-semibold flex-1">
                        {ipData.ip}
                      </span>
                      <Button variant="ghost" size="icon" onClick={() => copyToClipboard(ipData.ip)} title="Copy IP">
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setIpInput(ipData.ip ?? ""); setEditingIp(true); }}
                      >
                        Update
                      </Button>
                    </div>
                  )}
                  <div className="flex gap-4 flex-wrap">
                    <span className="text-xs text-muted-foreground">
                      Added: {new Date(ipData.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Updated: {new Date(ipData.updated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  </div>
                </div>
              ) : editingIp ? (
                <div className="flex gap-3 items-center">
                  <div className="flex-1">
                    <Label htmlFor="ip-address-new" className="sr-only">IP Address</Label>
                    <Input
                      id="ip-address-new"
                      placeholder="e.g. 203.0.113.50"
                      value={ipInput}
                      onChange={(e) => setIpInput(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={handleUpdateIp}
                    disabled={ipSaving || !(ipInput ?? "").trim()}
                  >
                    {ipSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setEditingIp(false)} disabled={ipSaving}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted border">
                  <span className="text-sm text-muted-foreground">No IP whitelisted</span>
                  <Button variant="outline" size="sm" onClick={() => { setIpInput(""); setEditingIp(true); }}>
                    Update
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="webhook">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Webhook className="h-5 w-5 text-primary" />
                  <h3 className="text-base font-semibold">Webhook</h3>
                </div>
                {!webhookLoading && !editingWebhook && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => startEditingWebhook(webhookData)}
                  >
                    {webhookData?.webhook ? "Update Webhook" : "Add Webhook"}
                  </Button>
                )}
              </div>
              <span className="text-sm text-muted-foreground mb-4 block">
                Receive real-time event notifications for orders, payments, and status changes.
              </span>
              <Separator className="mb-5" />

              <div className="flex items-start gap-3 rounded-lg border border-yellow-400/50 bg-yellow-50 p-3 text-yellow-800 dark:border-yellow-500/30 dark:bg-yellow-500/10 dark:text-yellow-300 mb-5">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold mb-0.5">Security Notice</p>
                  <p className="text-xs">Keep your webhook secret key secure and never share it. Use encryption for sensitive data. Verify webhook signatures to ensure requests are authentic.</p>
                </div>
              </div>

              {editingWebhook ? (
                /* Edit / Add form */
                <div className="flex flex-col gap-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="webhook-url">Webhook URL</Label>
                    <Input
                      id="webhook-url"
                      placeholder="https://your-server.com/webhook"
                      value={webhookForm.webhook}
                      onChange={(e) => setWebhookForm((f) => ({ ...f, webhook: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="webhook-secret">Secret Key</Label>
                    <Input
                      id="webhook-secret"
                      placeholder="Enter a secret key for signature verification"
                      value={webhookForm.secret}
                      onChange={(e) => setWebhookForm((f) => ({ ...f, secret: e.target.value }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Active</p>
                      <span className="text-xs text-muted-foreground">Enable or disable the webhook</span>
                    </div>
                    <Switch
                      checked={webhookForm.active}
                      onCheckedChange={(val) => setWebhookForm((f) => ({ ...f, active: val }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Encryption</p>
                      <span className="text-xs text-muted-foreground">Encrypt webhook payloads</span>
                    </div>
                    <Switch
                      checked={webhookForm.encryption}
                      onCheckedChange={(val) => setWebhookForm((f) => ({ ...f, encryption: val }))}
                    />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => setEditingWebhook(false)} disabled={webhookSaving}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveWebhook}
                      disabled={webhookSaving || !webhookForm.webhook.trim()}
                    >
                      {webhookSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                    </Button>
                  </div>
                </div>
              ) : webhookData?.webhook ? (
                /* Display existing webhook */
                <div className="flex flex-col gap-4">
                  <div>
                    <span className="text-xs text-muted-foreground font-medium mb-1 block">
                      Endpoint URL
                    </span>
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted border">
                      <span className="text-sm font-mono flex-1 break-all">
                        {webhookData.webhook}
                      </span>
                      <Button variant="ghost" size="icon" onClick={() => copyToClipboard(webhookData.webhook!)} title="Copy URL">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {webhookData.webhook_secret_key && (
                    <div>
                      <span className="text-xs text-muted-foreground font-medium mb-1 block">
                        Secret Key
                      </span>
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-muted border">
                        <span className={`text-sm font-mono flex-1 break-all ${showSecret ? "select-all" : "select-none"}`}>
                          {showSecret
                            ? webhookData.webhook_secret_key
                            : webhookData.webhook_secret_key.slice(0, 6) + "••••••••••••••••" + webhookData.webhook_secret_key.slice(-4)}
                        </span>
                        <Button variant="ghost" size="icon" onClick={() => setShowSecret((p) => !p)} title={showSecret ? "Hide" : "Reveal"}>
                          {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => copyToClipboard(webhookData.webhook_secret_key!)} title="Copy secret">
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="outline" className={webhookData.webhook_active ? "text-green-600 border-green-600" : ""}>
                      {webhookData.webhook_active ? "Active" : "Inactive"}
                    </Badge>
                    <Badge variant="outline" className={webhookData.webhook_active_encryption ? "text-primary border-primary" : ""}>
                      {webhookData.webhook_active_encryption ? "Encryption On" : "Encryption Off"}
                    </Badge>
                  </div>
                </div>
              ) : (
                /* No webhook configured */
                <div className="flex flex-col items-center gap-3 py-6">
                  <span className="text-sm text-muted-foreground text-center">
                    No webhook configured. Add one to receive real-time notifications.
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </Shimmer>

      <Toaster />
    </div>
  );
}
